import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Modal, Row } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  callNativeMobileApp,
  useAppContext,
  useToast,
  useTranslator,
} from '@front-activerse/common-components';
import {
  SCAN_LABEL_INTRO_FLAG,
  UI_FLAGS_COLLECTION,
  useCameraCapture,
  useScanLabelIntroSeen,
} from '../hooks';
import { AddUserProductFormValues } from '../AddUserProduct/helpers';
import {
  setPendingScanLabelPhotos,
  type ScanLabelAnalyzingState,
} from './TrackerScanLabelAnalyzingContainer';

type ScanStep = 'front' | 'nutrition';

const isScannerPath = (pathname: string) => pathname.endsWith('/scan-label');

interface TrackerScanLabelContainerProps {
  cancelScanLabel: () => void;
  mobileAppMode?: boolean;
  // The intro's "already seen" flag lives in Firestore, reached through the same per-brand helpers
  // the notification alerts use. Optional so a brand that has not wired them keeps working — the
  // intro then simply cannot be remembered.
  userId?: number;
  getClosedNotifications?: (collectionName: string, userId: number) => Promise<number[]>;
  markNotificationAsClosed?: (
    collectionName: string,
    userId: number,
    notificationId: number
  ) => Promise<void>;
}

const TrackerScanLabelContainer: React.FC<TrackerScanLabelContainerProps> = ({
  cancelScanLabel,
  mobileAppMode = false,
  userId = 0,
  getClosedNotifications,
  markNotificationAsClosed,
}) => {
  const { t, language } = useTranslator();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    formValues?: AddUserProductFormValues;
    fromAutoScan?: boolean;
    fromUserMeals?: boolean;
  } | null;
  const formValues = locationState?.formValues;
  // When launched straight from the action sheet (autoScan), an empty add-product form sits
  // between the tracker and this scanner. Closing should skip it and return to the tracker; when
  // opened from an actual add-product form, closing returns to that form.
  //
  // The path is consulted as well as the route state, because inside the native webview the state
  // never arrives: the sheet there is the NATIVE one (`open_bottom_sheet`), so the React `<Link
  // state={{ fromAutoScan: true }}>` is never clicked — the shell opens this screen with
  // `open_modal` carrying a bare `path`. `fromAutoScan` then read false and the X was hidden on
  // exactly the entry that is supposed to keep it. The two entries are distinguishable by URL:
  // the sheet uses the flat `/scan-label`, the form flow always nests under `add-product/`.
  const isSheetEntryPath = !location.pathname.includes('/add-product/');
  const fromAutoScan = locationState?.fromAutoScan ?? isSheetEntryPath;
  // Entered from the "Your meals" sheet rather than the tracker's: the product still gets its own
  // new meal, but that meal belongs to the user-meals base ("save to my meals"), not the daily log.
  //
  // Falls back to the path for the same reason `fromAutoScan` above does: the native sheet opens
  // `/meals/user-meals/scan-label` with `open_modal` and a bare path, so no state arrives and this
  // read false — the analysed product was then handed to the TRACKER add-product form and the meal
  // landed in the daily log instead of the meals base (IP-11148). Every user-meals route is mounted
  // under `/meals/user-meals/*`, so the prefix is what distinguishes them.
  const isUserMealsPath = location.pathname.startsWith('/meals/user-meals');
  const fromUserMeals = locationState?.fromUserMeals ?? isUserMealsPath;
  const { toastError } = useToast();

  const {
    images: {
      cameraShutter,
      closeWhiteIcon,
      flashOnIcon,
      flashOffIcon,
      photoGalleryIcon,
      videoPoster,
      scanLabelTipsImg,
      scanLabelTipsImgPl,
      scanLabelTipsImgSv,
      scanLabelTipsImgPt,
      scanLabelTipsImgCz,
      scanInfoIcon,
      doneIcon,
    },
  } = useAppContext();

  const camera = useCameraCapture();
  const [step, setStep] = useState<ScanStep>('front');
  // Which shot the NEXT capture belongs to, held in a ref alongside the state above.
  //
  // `step` alone cannot gate the branch in handleCapture: on Android a full-resolution q0.95
  // toBlob runs for seconds, and a second tap landing inside that window still reads the
  // pre-render `step === 'front'` from its closure. It then overwrote frontPhotoRef with a
  // second front shot, so the pair sent for analysis was the front of the product twice
  // (IP-11130). The ref flips synchronously, so the second tap sees 'nutrition' immediately.
  const stepRef = useRef<ScanStep>('front');
  // Synchronous mirror of camera.isCapturing. The hook's state flag only takes effect on the
  // next render — both the `disabled` prop and the guard reading it are blind for the whole
  // gap between the tap and that render, which is exactly the multi-second window above.
  const isCapturingRef = useRef(false);
  // Intro bottom sheet ("Take 2 photos"): shown automatically on the first ever visit and
  // re-openable any time via the "i" button — same content in both cases.
  //
  // "First visit ever" is answered by Firestore, not localStorage: the Android WebView drops DOM
  // storage when the shell tears it down, so the old flag was unset on every app launch and the
  // sheet reopened each time (IP-11131). `hasSeenIntro` is null until Firestore replies.
  const { hasSeenIntro, setHasSeenIntro } = useScanLabelIntroSeen(userId, getClosedNotifications);
  // Opened by hand via "i" — tracked separately so dismissing a manual open does not have to write
  // the flag again, and so the automatic open can wait on Firestore without blocking this one.
  const [isIntroOpenedManually, setIsIntroOpenedManually] = useState(false);
  // Hidden while the answer is unknown: flashing the sheet and pulling it away is worse than a
  // beat of nothing, and this resolves from cache in the usual case.
  const showIntro = isIntroOpenedManually || hasSeenIntro === false;
  const [frontThumbUrl, setFrontThumbUrl] = useState<string | null>(null);
  const frontPhotoRef = useRef<File | null>(null);
  const scanAreaRef = useRef<HTMLDivElement | null>(null);
  // Mirror of the thumb URL so the route-cleanup below can revoke whatever is current without
  // taking it as an effect dependency (which would re-run the camera lifecycle on every shot).
  const frontThumbUrlRef = useRef<string | null>(null);
  frontThumbUrlRef.current = frontThumbUrl;

  // Camera lifecycle — tied to this being the visible route, NOT to mount/unmount:
  // SlideRoutes renders with destroy={false}, so this component stays mounted after
  // navigating away (back gesture, TitleBar back, successful scan). A mount-effect
  // cleanup would never run and the device camera would stay on.
  //
  // The page keys this component on `location.key`, which makes a NEW visit a fresh mount. Going
  // BACK, though, restores the entry this instance was already mounted for — same key, same
  // instance — so returning from the analysis route has to reset the step explicitly. Without it the
  // user landed on a stale 2/2 with the previous front photo still pinned.
  const isActiveRoute = isScannerPath(location.pathname);

  // The warmup window: `startCamera` flips `isSearchingCamera` on while it enumerates devices
  // looking for one with a torch, then flips it off as `isCameraActive` comes on. Both flags are
  // needed — the pair is false/false before the effect below has even run, which must not read as
  // "ready". Camera errors are rendered by their own branch, so they are excluded here.
  const isCameraWarmingUp = !camera.error && !camera.isCameraActive && camera.isSearchingCamera;

  useEffect(() => {
    if (!isActiveRoute) return undefined;
    let active = true;
    const init = async () => {
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (active) await camera.startCamera();
    };
    init();
    const stop = () => camera.stopCamera();
    // While this page is kept mounted, its `useLocation()` never changes — verified by logging
    // `location.pathname`/`location.key` across a full flow: both stay pinned to the entry it first
    // rendered for, while `window.location` moves. So navigation has to come from popstate.
    const handlePopState = () => {
      if (!isScannerPath(window.location.pathname)) {
        camera.stopCamera();
        return;
      }
      // Back onto the scanner (from the analysis route) restarts at step 1/2 with the front of the
      // product — IP-11084 case 1. Without this the user landed on a stale 2/2 with the previous
      // front photo still pinned.
      if (frontThumbUrlRef.current) URL.revokeObjectURL(frontThumbUrlRef.current);
      frontPhotoRef.current = null;
      setFrontThumbUrl(null);
      // The ref is the one handleCapture branches on, so resetting only the state would show
      // "1/2 · front of product" while the next shot was still filed as the nutrition label.
      stepRef.current = 'front';
      isCapturingRef.current = false;
      setStep('front');
      camera.startCamera();
    };
    // Mobile -> Web (IP-11126): the system back gesture dismisses the modal without the X ever
    // being tapped, so the shell tells the page to let go of the camera. Only the release is done
    // here — the shell is already closing itself, so echoing `close_modal` back would be a
    // redundant round-trip (this is why it does not just reuse the X's handler).
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.action === 'release_camera_and_close_modal') camera.stopCamera();
    };
    window.addEventListener('beforeunload', stop);
    window.addEventListener('pagehide', stop);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('message', handleMessage);
    return () => {
      active = false;
      camera.stopCamera();
      window.removeEventListener('beforeunload', stop);
      window.removeEventListener('pagehide', stop);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('message', handleMessage);
      if (frontThumbUrlRef.current) URL.revokeObjectURL(frontThumbUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute]);

  // Granting camera access from the browser's own settings leaves the "no access" alert on screen
  // next to a live preview: the lifecycle effect above runs once per route entry, so nothing calls
  // startCamera() again and the hook's error (only cleared on startCamera) goes stale. Retry when
  // the permission flips to granted, and — because Safari does not expose 'camera' to the
  // Permissions API — also when the tab regains focus, which is when the user comes back from the
  // settings UI. The retry is a no-op unless we are actually holding an error.
  useEffect(() => {
    if (!isActiveRoute) return undefined;

    const retryIfBlocked = () => {
      if (!camera.error) return;
      camera.startCamera();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') retryIfBlocked();
    };

    window.addEventListener('focus', retryIfBlocked);
    document.addEventListener('visibilitychange', handleVisibility);

    // Chrome/Edge/Firefox report camera permission changes live, so recovery happens the moment
    // the user flips the toggle without waiting for a tab switch.
    let permissionStatus: PermissionStatus | null = null;
    const onPermissionChange = () => {
      if (permissionStatus?.state === 'granted') retryIfBlocked();
    };
    navigator.permissions
      ?.query({ name: 'camera' as PermissionName })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener('change', onPermissionChange);
      })
      // Safari rejects on the unsupported 'camera' name — the listeners above still cover it.
      .catch(() => undefined);

    return () => {
      window.removeEventListener('focus', retryIfBlocked);
      document.removeEventListener('visibilitychange', handleVisibility);
      permissionStatus?.removeEventListener('change', onPermissionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveRoute, camera.error]);

  // Blob URLs are revoked explicitly — when replaced by a new shot and when the route is left (see
  // the lifecycle effect above). Deliberately NOT in a `[thumbUrl]`-keyed cleanup: that also fires
  // whenever the component re-renders its way out of a kept-mounted state (SlideRoutes
  // destroy={false}), which revoked the very blob still sitting in an <img src> and left grey
  // placeholders behind.

  const dismissIntro = () => {
    setIsIntroOpenedManually(false);
    // Optimistic: the sheet closes on tap and does not wait for the write. A failed write only
    // means the intro opens once more, which is the harmless direction to fail in.
    setHasSeenIntro(true);
    if (markNotificationAsClosed && userId) {
      markNotificationAsClosed(UI_FLAGS_COLLECTION, userId, SCAN_LABEL_INTRO_FLAG);
    }
  };

  const openIntro = () => setIsIntroOpenedManually(true);

  // The tips image carries baked-in label text, so it has to follow the UI language. Mirrors how the
  // designers ship it: `tracker_scan_label_tips_img.png` is the brand's base artwork, and a
  // per-language file exists only where they provided one (today: `_pl` on dtba and maxx). Anything
  // without its own file falls back to the base — for the Polish-only brands (oxy, hashimoto) that
  // base IS the Polish artwork, which is what those Drive folders contain.
  //
  // A map rather than a switch: the per-language entries are optional AND brand-specific, so adding
  // e.g. a Portuguese file for radiance is one import in that brand's images.js — this shared
  // component needs no change, and the other seven brands keep falling back to their base.
  const localisedTipsImg: Record<string, string | undefined> = {
    'pl-PL': scanLabelTipsImgPl,
    'sv-SE': scanLabelTipsImgSv,
    'pt-BR': scanLabelTipsImgPt,
    'cs-CZ': scanLabelTipsImgCz,
  };
  const tipsImg = localisedTipsImg[language] ?? scanLabelTipsImg;

  // Both photos taken — hand them to the analysis route. Splitting the analysis off onto its own
  // history entry is what makes back-during-analysis return here (see the scan-plate pair
  // `/scan-plate` -> `/scan-plate/details`), instead of popping the whole scanner off the stack.
  const startAnalysis = async (front: File, nutrition: File) => {
    await camera.stopCamera();
    // The photos go through a module-level holder, NOT location.state: `File` cannot be cloned into
    // history state, so navigating back to the analysis route would restore it empty and the screen
    // would look photo-less (which sent the user out to the camera).
    setPendingScanLabelPhotos(front, nutrition);
    const analyzingState: ScanLabelAnalyzingState = {
      ...(formValues && { formValues }),
      fromAutoScan,
      fromUserMeals,
    };
    // `/analyzing` is stripped before appending it: SlideRoutes (destroy={false}) keeps this
    // component mounted with a FROZEN render location, so on a second scan `location.pathname`
    // still read `…/scan-label/analyzing` from the previous run and this produced
    // `…/analyzing/analyzing` — a route that matches nothing, which dropped the state (and with it
    // the photos) and reopened the add-product form empty.
    const scannerPath = location.pathname.replace(/\/analyzing$/, '');
    navigate(`${scannerPath}/analyzing`, { state: analyzingState });
  };

  // Branches on the ref, not on `step`: see stepRef above. `step` stays as the render-time copy
  // that drives the pill and the thumb.
  const handleCapture = async (file: File) => {
    if (stepRef.current === 'front') {
      frontPhotoRef.current = file;
      setFrontThumbUrl((previous) => {
        if (previous) URL.revokeObjectURL(previous);
        return URL.createObjectURL(file);
      });
      stepRef.current = 'nutrition';
      setStep('nutrition');
    } else if (stepRef.current === 'nutrition' && frontPhotoRef.current) {
      await startAnalysis(frontPhotoRef.current, file);
    }
  };

  const handleShutter = async () => {
    // Both flags are checked: the ref closes the pre-render window that lets a second tap
    // through, the state flag keeps the existing behaviour for anything that only re-renders.
    if (isCapturingRef.current || camera.isCapturing) return;
    isCapturingRef.current = true;
    try {
      // Crop exactly to the white scan frame the user aims with, at full source resolution
      // (no downscaling) and high JPEG quality — the nutrition table fills the sent image,
      // which is what the model actually needs to read small print.
      const file = await camera.capturePhoto({
        crop: 'element',
        cropElement: scanAreaRef.current,
        quality: 0.95,
        fileName: stepRef.current === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
      });
      await handleCapture(file);
    } catch {
      toastError();
    } finally {
      // Released even on failure — an exception used to leave the ref pinned and the shutter
      // permanently dead, with no way back other than leaving the screen.
      isCapturingRef.current = false;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Same synchronous guard as the shutter — ScanPlate gates its upload handler the same way.
    // Without it a gallery pick during a running capture raced the shutter into handleCapture.
    if (isCapturingRef.current) return;
    isCapturingRef.current = true;
    try {
      // Gallery uploads have no scan frame to crop to — send the whole photo, but keep it under
      // the backend's 1MB-per-image limit (1600px + q0.92 instead of the legacy 1024px + q0.9).
      const file = await camera.processUploadedFile(event, {
        crop: 'scale',
        maxDimension: 1600,
        quality: 0.92,
        fileName: stepRef.current === 'front' ? 'label-front.jpg' : 'label-nutrition.jpg',
      });
      if (file) await handleCapture(file);
    } finally {
      isCapturingRef.current = false;
    }
  };

  const handleClose = async () => {
    cancelScanLabel();
    await camera.stopCamera();
    // Give the track a moment to actually be released before the shell tears the webview down —
    // same 100ms ScanPlate and the barcode scanner wait, without which the device camera (and on
    // some Androids the indicator light) stayed busy.
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Inside the native webview this route is presented as a modal that only the shell can
    // dismiss; releasing the camera merely blanked the preview and left the modal up with no way
    // out (IP-11126). Harmless on the plain web — the helper no-ops outside the app.
    callNativeMobileApp({ action: 'close_modal' });

    // The scanner is entered directly (no add-product launcher in between) in BOTH flows now, so a
    // single back step is correct: autoScan came straight from the tracker, and the form flow came
    // from the add-product form. navigate(-1) also keeps the SlideRoutes slide-out animation.
    navigate(-1);
  };

  return (
    <div className="scan-plate">
      <video
        ref={camera.videoRef}
        className="camera-video-fullscreen"
        poster={videoPoster}
        playsInline
        muted
        style={{ opacity: camera.isVideoVisible ? 1 : 0 }}
      />
      <canvas ref={camera.canvasRef} className="d-none" />
      <input
        ref={camera.fileInputRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFileUpload}
      />

      <div className="camera-overlay-fullscreen">
        {camera.isCameraActive && <div className="scan-area" ref={scanAreaRef} />}

        {/* Tied to `isCameraActive`, not just to the absence of an error: the thumb is positioned
            against the scan frame (`captured-photo-anchored`), so showing it while the frame is
            gone during warmup left it floating over a black screen. */}
        {frontThumbUrl && step === 'nutrition' && !camera.error && camera.isCameraActive && (
          <div
            className="captured-photo captured-photo-anchored"
            data-testid="scan-label-front-thumb"
            data-test-id="scan_label_front_thumb"
          >
            <img src={frontThumbUrl} alt="" />
            <img src={doneIcon} alt="" className="captured-photo-badge" />
          </div>
        )}

        <div className="top-controls">
          {/* The X is dropped only where something else already closes the screen: inside the native
              webview, where this route opens in a modal that carries its own dismiss, AND only for the
              add-product-form entry (IP-11122 cases 2 and 3). On the plain web there is no such modal
              chrome, so removing it left no easy way out — hence the mobileAppMode condition rather
              than the entry alone. The action-sheet entry keeps it everywhere (case 1). */}
          {fromAutoScan || !mobileAppMode ? (
            <button
              className="scan-plate-button"
              onClick={handleClose}
              data-testid="scan-label-close"
              data-test-id="scan_label_close"
            >
              <img src={closeWhiteIcon} alt="" />
            </button>
          ) : (
            // A spacer the exact width of the button it replaces. An empty div collapsed to 0 and,
            // because top-controls is space-between, pulled the step pill 16px off centre — half the
            // 32px the button occupies. Sized by hiding the same icon rather than a hard-coded width,
            // so it tracks whatever the brand's asset measures.
            <div aria-hidden="true" className="scan-plate-button invisible">
              <img src={closeWhiteIcon} alt="" />
            </div>
          )}
          {/* Held back until the camera is live, like every other overlay element (IP-11132): the
              warmup screen in Figma 4401:9175 is bare apart from the X, the "i" and the spinner.
              Announcing "1/2 · front of product" over a black rectangle promised a viewfinder that
              was not there yet. */}
          {isCameraWarmingUp ? (
            <div aria-hidden="true" />
          ) : (
            <p className="mb-0 scan-steps-pill">
              <span className="semi-bold">
                {t('tracker.scanner.steps.current', {
                  current: step === 'front' ? 1 : 2,
                  total: 2,
                })}
              </span>{' '}
              {/* Graphic separator, not copy — kept out of the locale files for the same reason the
                  tick mark was: there is nothing to translate. */}
              ·{' '}
              <span>
                {step === 'front'
                  ? t('tracker.scanner.steps.frontOfProduct')
                  : t('tracker.scanner.steps.nutritionLabel')}
              </span>
            </p>
          )}
          <button
            className="scan-plate-button"
            onClick={openIntro}
            data-testid="scan-label-info"
            data-test-id="scan_label_info"
            aria-label={t('tracker.scanner.intro.title')}
          >
            <img src={scanInfoIcon} alt="" />
          </button>
        </div>

        <div className="bottom-controls">
          {/* Analysis failures are not handled here: they always carry step === 'analyzing', so the
              earlier return renders them on the analyzing screen with the photos still visible. */}
          {camera.error ? (
            <div className="text-center w-100">
              <Alert color="danger" className="mb-0" fade={false}>
                <p className="mb-0 text-text-main-color text-start">
                  {t('tracker.scanFood.noAccessCamera.notification')}
                </p>
              </Alert>
              {/* No gallery button here on purpose: without camera permission the browser file
                  picker is the only way in, and offering it next to the "no access" alert reads as
                  a working fallback. Scan food (ScanPlate) hides it the same way. */}
              {!camera.isCameraActive && !camera.isSearchingCamera && (
                <Button
                  color="link"
                  size="sm"
                  onClick={() => window.location.reload()}
                  disabled={camera.isSearchingCamera || camera.isCapturing}
                >
                  <p>{t('tracker.scan.button.refreshCamera')}</p>
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Torch needs a live track to toggle, so it is greyed out (rendered, disabled)
                  rather than swapped for a spacer while the camera warms up — matching ScanPlate
                  and the disabled bolt in Figma 4401:9175. */}
              {camera.isCameraActive && camera.isTorchSupported ? (
                <button
                  className="scan-plate-button"
                  onClick={camera.toggleTorch}
                  disabled={camera.isCapturing}
                >
                  <img src={camera.isTorchOn ? flashOnIcon : flashOffIcon} alt="" />
                </button>
              ) : (
                <button className="scan-plate-button" disabled>
                  <img src={flashOffIcon} alt="" />
                </button>
              )}

              {isCameraWarmingUp && (
                <p className="mb-0">
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  {t('tracker.scan.findingCamera')}
                </p>
              )}

              {/* The shutter only exists once there is something to shoot. It used to render
                  immediately, so tapping it during warmup ran capturePhoto() against a video
                  element with no stream — a black frame sent off for analysis. */}
              {camera.isCameraActive && !camera.isSearchingCamera && (
                <button
                  className="scan-plate-button"
                  onClick={handleShutter}
                  disabled={camera.isCapturing}
                  data-testid="scan-label-shutter"
                  data-test-id="scan_label_shutter"
                >
                  {/* Processing a full-resolution q0.95 crop takes seconds on Android, and a
                      shutter that only greys out read as a frozen screen — testers tapped it
                      again (IP-11130). The spinner replaces the icon for that whole window. */}
                  {camera.isCapturing ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <img src={cameraShutter} alt="" />
                  )}
                </button>
              )}

              <button
                className="scan-plate-button"
                onClick={camera.openGallery}
                disabled={camera.isCapturing || camera.isSearchingCamera}
              >
                <img src={photoGalleryIcon} alt="" />
              </button>
            </>
          )}
        </div>
      </div>

      {showIntro && (
        // Same bottom sheet as the barcode scanner's tips (Reactstrap Modal + `.select-insert-date`):
        // full-bleed, pinned to the bottom edge, rounded only on top. The bespoke backdrop/sheet pair
        // this replaced sat inset from the sides with all four corners rounded.
        <Modal
          className="from-bottom"
          isOpen={showIntro}
          fullscreen={true}
          size="lg"
          toggle={dismissIntro}
        >
          <div
            className="select-insert-date"
            data-testid="scan-label-intro"
            data-test-id="scan_label_intro"
          >
            <Container>
              <Row>
                <Col xs="12" md={{ size: 4, offset: 4 }}>
                  {/* One shipped illustration instead of two thumbs — it already shows both shots
                      side by side, with the good/bad marks and the label copy baked in (hence
                      per-language). The numbered captions sit below it, one per half, as in Figma
                      8838:17597 (label y=162 against a 150px-tall image). */}
                  <img src={tipsImg} alt="" className="scan-label-intro-img" />
                  <div className="scan-label-intro-captions">
                    <p className="description mb-0">
                      {t('tracker.scanner.intro.imageLabel.front')}
                    </p>
                    <p className="description mb-0">
                      {t('tracker.scanner.intro.imageLabel.nutrition')}
                    </p>
                  </div>

                  <h2 className="title-small bold text-center mt-l mb-xs">
                    {t('tracker.scanner.intro.title')}
                  </h2>
                  <p className="text-text-variant-color text-center mb-l">
                    {t('tracker.scanner.intro.description')}
                  </p>

                  <Button
                    color="primary"
                    className="w-100"
                    onClick={dismissIntro}
                    data-testid="scan-label-intro-cta"
                    data-test-id="scan_label_intro_cta"
                  >
                    {t('tracker.scanner.intro.cta')}
                  </Button>
                </Col>
              </Row>
            </Container>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TrackerScanLabelContainer;
